import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Spinner from "react-bootstrap/Spinner";
import { pdf, Font } from "@react-pdf/renderer";
import QRCode from "qrcode";
import PropertyQrDocument, {
  DEFAULT_PROPERTY_QR_COPY,
  PROPERTY_QR_DESIGNS,
} from "./PropertyQrDocument";
import CustomizeDesignModal from "./CustomizeDesignModal";
import { buildCustomDocument } from "./customDesignRuntime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faMapMarkerAlt,
  faFilePdf,
  faDownload,
  faTimes,
  faBed,
  faBath,
  faCar,
  faMagicWandSparkles,
  faPenToSquare,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { handleTokenAndPostRequest } from "../../utils/utils";

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  fontSize: 14,
  boxSizing: "border-box",
  outline: "none",
  background: "#fff",
};

const labelStyle = {
  display: "block",
  fontWeight: 600,
  fontSize: 13,
  color: "#374151",
  marginBottom: 6,
};

function useDebouncedValue(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function PropertyQrTab({ isMobile }) {
  // Street search
  const [streetQuery, setStreetQuery] = useState("");
  const debouncedQuery = useDebouncedValue(streetQuery, 250);
  const [streetResults, setStreetResults] = useState([]);
  const [streetLoading, setStreetLoading] = useState(false);
  const [selectedStreet, setSelectedStreet] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const inputWrapRef = useRef(null);
  const [dropdownRect, setDropdownRect] = useState(null);

  // Recompute the dropdown's screen position whenever it's open
  // (and on scroll/resize) so it follows the input correctly.
  useLayoutEffect(() => {
    if (!showDropdown) return undefined;
    const update = () => {
      if (!inputWrapRef.current) return;
      const r = inputWrapRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [showDropdown, streetQuery]);

  // Property list
  const [properties, setProperties] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [propertyError, setPropertyError] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Filters (applied client-side over the full street list)
  const [startNum, setStartNum] = useState("");
  const [endNum, setEndNum] = useState("");
  const [minBeds, setMinBeds] = useState("");
  const [propertyTypes, setPropertyTypes] = useState(() => new Set());
  const [occupancyMode, setOccupancyMode] = useState("all"); // all | rented | owner
  const [parityMode, setParityMode] = useState("all"); // all | odd | even

  // Design selection — built-in IDs are short strings ("classic", "bold", …);
  // custom designs use their server UUID. Both live in this same state.
  const [design, setDesign] = useState(PROPERTY_QR_DESIGNS[0].id);

  // Brand typography. When the agent has set `agent_font_url` in Settings we
  // register it with @react-pdf under a stable family name (`AgentBrandFont`)
  // and let the agent opt in via a toggle. Mirroring it onto the DOM via a
  // <style>@font-face lets us show a small preview without the PDF renderer.
  const AGENT_FONT_FAMILY = "AgentBrandFont";
  const [agentFontUrl, setAgentFontUrl] = useState("");
  const [agentFontReady, setAgentFontReady] = useState(false);
  const [useAgentFont, setUseAgentFont] = useState(false);

  // Agent-uploaded fallback hero image. When a property has no photo we drop
  // this into `photo_url_full` so the card renders the same with-photo layout
  // instead of the text-only no-image block. Held as a base64 data URL so the
  // PDF renderer never has to fetch it. Loaded from /property-qr-context (the
  // backend inlines it) and refreshed in place on upload.
  const [placeholderDataUrl, setPlaceholderDataUrl] = useState("");
  const [placeholderUploading, setPlaceholderUploading] = useState(false);
  const [placeholderError, setPlaceholderError] = useState("");
  const placeholderInputRef = useRef(null);

  useEffect(() => {
    if (!agentFontUrl) {
      setAgentFontReady(false);
      return undefined;
    }
    // Register with @react-pdf. Designs use bold/italic Text freely; if we
    // only register a single src, react-pdf silently hangs on the missing
    // weight/style lookup. Point every common variant at the same file so
    // the renderer always finds *something*.
    try {
      Font.register({
        family: AGENT_FONT_FAMILY,
        fonts: [
          { src: agentFontUrl },
          { src: agentFontUrl, fontWeight: 400 },
          { src: agentFontUrl, fontWeight: 500 },
          { src: agentFontUrl, fontWeight: 600 },
          { src: agentFontUrl, fontWeight: 700 },
          { src: agentFontUrl, fontWeight: 800 },
          { src: agentFontUrl, fontStyle: "italic" },
          { src: agentFontUrl, fontWeight: 700, fontStyle: "italic" },
        ],
      });
      // No hyphenation — a missing dictionary for a custom font can hang the
      // renderer on certain text fragments. We don't need word-splitting on
      // print cards anyway.
      try { Font.registerHyphenationCallback((word) => [word]); } catch {}
      setAgentFontReady(true);
    } catch {
      setAgentFontReady(false);
    }
    // Default the checkbox to on the first time we discover a brand font —
    // an agent who bothered to upload one almost always wants it used.
    setUseAgentFont(true);
    // Mirror onto the DOM for the small preview swatch.
    const styleEl = document.createElement("style");
    styleEl.dataset.agentBrandFont = "1";
    styleEl.textContent = `@font-face { font-family: "${AGENT_FONT_FAMILY}"; src: url("${agentFontUrl}"); font-display: swap; }`;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, [agentFontUrl]);

  // ─── TEMP DEBUG (brand-font glyph-drop diagnosis — REMOVE once resolved) ───
  // Captures the exact font bytes the browser registered + fontkit's in-browser
  // parse, so we can diff against the font tested offline. Generate a PDF first
  // (so the brand font is fetched/registered), then run `__fontDebug()` in the
  // devtools console and send back the two downloaded files + the logged JSON.
  const runFontDebug = async () => {
    const dl = (name, blob) => {
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = name;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(u);
    };
    const report = {
      agentFontUrl_prefix: (agentFontUrl || "").slice(0, 64),
      agentFontUrl_length: (agentFontUrl || "").length,
      useAgentFont, agentFontReady, design,
    };
    try {
      let bytes;
      if (agentFontUrl && agentFontUrl.startsWith("data:")) {
        const bin = atob(agentFontUrl.split(",")[1]);
        bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        report.source = "data-url";
      } else if (agentFontUrl) {
        bytes = new Uint8Array(await (await fetch(agentFontUrl)).arrayBuffer());
        report.source = "fetched";
      } else {
        report.error = "agentFontUrl is empty — generate a PDF first, then re-run.";
      }
      if (bytes) {
        report.fontBytes_length = bytes.length;
        report.sfnt_hex = Array.from(bytes.slice(0, 4))
          .map((b) => b.toString(16).padStart(2, "0")).join(" ");
        dl("app_registered_font.bin", new Blob([bytes]));
        const fk = await import("fontkit");
        const fontkit = fk.default || fk;
        const font = fontkit.create(bytes);
        report.fontkit = {
          type: font.type,
          postscriptName: font.postscriptName,
          familyName: font.familyName,
          numGlyphs: font.numGlyphs,
          characterSet_length: font.characterSet && font.characterSet.length,
        };
        try { report.post_version = font["post"] && font["post"].version; } catch (e) {}
        report.firstGlyphNames = [];
        for (let i = 0; i < 8; i++) {
          try { report.firstGlyphNames.push(font.getGlyph(i).name); }
          catch (e) { report.firstGlyphNames.push("?"); }
        }
        const run = font.layout("9 Mary Brown Place");
        report.layout = run.glyphs.map((g) => ({
          id: g.id,
          cp: (g.codePoints || []).map((c) => String.fromCodePoint(c)).join(""),
          adv: g.advanceWidth,
        }));
      }
    } catch (e) {
      report.error = String((e && e.stack) || e);
    }
    // Render a single failing card through the real pdf() path with this exact
    // font, so the produced PDF's input bytes are known (app_registered_font.bin).
    try {
      if (agentFontReady && useAgentFont) {
        const mockAgent = {
          agent_name: "Zander van Oldenborgh", agent_title: "Licensed Agent",
          agent_phone: "0434 816 691", agent_email: "zander@atlas.com.au",
          agency_name: "Atlas | Byron Bay", agent_colour: "#1a1a1a",
        };
        const mockProps = [{
          obfuscated_id: "dbg", street_number: "9", street_name: "Mary Brown",
          street_type_long: "Place", suburb: "Blair Athol", state: "NSW",
          postcode: "2560", listing_property_type: "House",
          photo_url_full: "", qr_data_url: "",
        }];
        const blob = await pdf(
          <PropertyQrDocument
            agent={mockAgent}
            properties={mockProps}
            copy={copy}
            design="classic"
            agentFontFamily={AGENT_FONT_FAMILY}
          />
        ).toBlob();
        dl("app_failing_card.pdf", blob);
        report.cardRendered = true;
      } else {
        report.cardRendered = false;
        report.cardSkipReason = "brand font not active (toggle it on + generate first)";
      }
    } catch (e) {
      report.cardError = String((e && e.stack) || e);
    }
    // eslint-disable-next-line no-console
    console.log("FONT_DEBUG", JSON.stringify(report, null, 2));
    dl("font_debug_report.json",
      new Blob([JSON.stringify(report, null, 2)], { type: "application/json" }));
    return report;
  };
  useEffect(() => {
    window.__fontDebug = runFontDebug;
  });
  // ─── END TEMP DEBUG ───

  // Agency-shared custom designs loaded from the server. `customizeFor`
  // controls the Customize modal — null when closed, an object describing
  // either a new-from-base or edit-existing flow when open.
  const [customDesigns, setCustomDesigns] = useState([]);
  const [customizeFor, setCustomizeFor] = useState(null);

  useEffect(() => {
    handleTokenAndPostRequest(
      `${window.api_host}/property-qr-design/list`,
      {}
    )
      .then((res) => {
        if (res.data?.status === "success" && Array.isArray(res.data.designs)) {
          setCustomDesigns(res.data.designs);
        }
      })
      .catch(() => {
        // Non-fatal — agent can still use built-in designs.
      });
  }, []);

  const allDesigns = useMemo(
    () => [
      ...PROPERTY_QR_DESIGNS.map((d) => ({ ...d, kind: "builtin" })),
      ...customDesigns.map((d) => ({
        id: d.id,
        name: d.name,
        description: `Customised from ${d.base_design_id}`,
        kind: "custom",
        custom: d,
      })),
    ],
    [customDesigns]
  );

  const findCustomDesign = (id) => customDesigns.find((d) => d.id === id);

  const handleDeleteCustom = async (id) => {
    if (!window.confirm("Delete this custom design? This can't be undone.")) return;
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/property-qr-design/delete`,
        { id }
      );
      if (res.data?.status === "success") {
        setCustomDesigns((prev) => prev.filter((d) => d.id !== id));
        if (design === id) setDesign(PROPERTY_QR_DESIGNS[0].id);
      }
    } catch {
      // ignore
    }
  };

  // Sample data + previews for the design picker
  const [sample, setSample] = useState(null);
  const sampleReqRef = useRef(0);
  const [previewUrls, setPreviewUrls] = useState({});
  const previewReqRef = useRef(0);
  const [modalDesign, setModalDesign] = useState(null);

  // Copy overrides
  // Copy overrides were a templated-variable UI from before the AI editor —
  // they're gone now (the agent edits headlines/chips/etc. by re-prompting
  // the design). We still hand the defaults to the renderers since the
  // built-in designs read them.
  const copy = DEFAULT_PROPERTY_QR_COPY;

  // PDF
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Typeahead query
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setStreetResults([]);
      return;
    }
    setStreetLoading(true);
    handleTokenAndPostRequest(`${window.api_host}/street-suggest`, {
      query: debouncedQuery,
    })
      .then((res) => {
        setStreetResults(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => setStreetResults([]))
      .finally(() => setStreetLoading(false));
  }, [debouncedQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    function onClick(e) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        !(e.target.closest && e.target.closest("[data-street-dropdown]"))
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const resetForNewStreet = () => {
    setProperties([]);
    setSelectedIds(new Set());
    // Drop the cached sample so the design previews rebuild from the new
    // street's properties (the sample effect bails out while `sample` is set).
    setSample(null);
    setPdfBlob(null);
    setPdfUrl(null);
    setStartNum("");
    setEndNum("");
    setMinBeds("");
    setPropertyTypes(new Set());
    setOccupancyMode("all");
    setParityMode("all");
    setPropertyError("");
  };

  const fetchProperties = async (slug) => {
    if (!slug) return;
    setPropertyLoading(true);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/street-listing`,
        { urlSlug: slug, fetchAll: true }
      );
      const all = Array.isArray(res.data?.data) ? res.data.data : [];
      setProperties(all);
      // Auto-deselect properties flagged as currently rented.
      setSelectedIds(
        new Set(all.filter((p) => !p.is_rental).map((p) => p.obfuscated_id))
      );
    } catch (err) {
      setPropertyError("Couldn't load properties. Please try again.");
    }
    setPropertyLoading(false);
  };

  const handleSelectStreet = (s) => {
    setSelectedStreet(s);
    setStreetQuery(s.address || s.urlSlug || "");
    setShowDropdown(false);
    resetForNewStreet();
    fetchProperties(s.urlSlug);
  };

  const handleClearStreet = () => {
    setSelectedStreet(null);
    setStreetQuery("");
    setStreetResults([]);
    resetForNewStreet();
  };

  // Distinct property types in the current street listing — drives the
  // filter chips below.
  const availableTypes = useMemo(() => {
    const set = new Set();
    properties.forEach((p) => {
      if (p.listing_property_type) set.add(p.listing_property_type);
    });
    return Array.from(set).sort();
  }, [properties]);

  const filteredProperties = useMemo(() => {
    const lo = startNum === "" ? null : parseInt(startNum, 10);
    const hi = endNum === "" ? null : parseInt(endNum, 10);
    const beds = minBeds === "" ? null : parseInt(minBeds, 10);
    const filtered = properties.filter((p) => {
      if (occupancyMode === "rented" && !p.is_rental) return false;
      if (occupancyMode === "owner" && p.is_rental) return false;
      if (propertyTypes.size > 0 && !propertyTypes.has(p.listing_property_type)) {
        return false;
      }
      if (beds != null && !Number.isNaN(beds)) {
        const b = parseInt(p.bedrooms, 10);
        if (Number.isNaN(b) || b < beds) return false;
      }
      const n = parseInt(p.street_number, 10);
      const validN = !Number.isNaN(n);
      if (parityMode === "odd" && (!validN || n % 2 === 0)) return false;
      if (parityMode === "even" && (!validN || n % 2 !== 0)) return false;
      if (lo != null && !Number.isNaN(lo) && (!validN || n < lo)) return false;
      if (hi != null && !Number.isNaN(hi) && (!validN || n > hi)) return false;
      return true;
    });
    // Door-knock order: low odd numbers ascending, then high even numbers
    // descending — so an agent walking the street covers one side then
    // doubles back along the other.
    return filtered.slice().sort((a, b) => {
      const an = parseInt(a.street_number, 10);
      const bn = parseInt(b.street_number, 10);
      const aValid = !Number.isNaN(an);
      const bValid = !Number.isNaN(bn);
      if (!aValid && !bValid) return 0;
      if (!aValid) return 1;
      if (!bValid) return -1;
      const aEven = an % 2 === 0;
      const bEven = bn % 2 === 0;
      if (aEven !== bEven) return aEven ? 1 : -1; // odds first
      // Same side: odds asc, evens desc.
      return aEven ? bn - an : an - bn;
    });
  }, [
    properties,
    startNum,
    endNum,
    minBeds,
    propertyTypes,
    occupancyMode,
    parityMode,
  ]);

  const togglePropertyType = (t) => {
    setPropertyTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Convert a remote image URL into a base64 data URL so @react-pdf/renderer
  // doesn't try to fetch it itself (which fails on CORS / 404 / mixed content
  // and tears down the entire PDF render). Returns null on any failure so the
  // caller can fall back to a non-image variant.
  const urlToDataUrl = async (url) => {
    if (!url) return null;
    if (url.startsWith("data:")) return url;
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      try {
        return await new Promise((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = "anonymous";
          image.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            canvas.getContext("2d").drawImage(image, 0, 0);
            resolve(canvas.toDataURL("image/png"));
          };
          image.onerror = reject;
          image.src = url;
        });
      } catch {
        return null;
      }
    }
  };

  // Normalise an arbitrary image File into a baseline-PNG data URL via canvas.
  // @react-pdf only decodes baseline PNG/JPEG — it renders a blank white box on
  // GIF/WebP/progressive/CMYK sources. Drawing the decoded image back out
  // through a canvas re-encodes whatever the agent picked into a format the PDF
  // renderer can embed. Returns "" if the browser can't even decode the file.
  const fileToPngDataUrl = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext("2d").drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
          } catch {
            resolve("");
          }
        };
        img.onerror = () => resolve("");
        img.src = reader.result;
      };
      reader.onerror = () => resolve("");
      reader.readAsDataURL(file);
    });

  const dataUrlToBlob = (dataUrl) => {
    const [head, b64] = dataUrl.split(",");
    const mime = (head.match(/data:([^;]+)/) || [])[1] || "image/png";
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  };

  // Upload (or replace) the agent's fallback hero image. Reuses the shared
  // /update-agent-image endpoint — the same one Settings uses for the logo and
  // profile photo — under a dedicated `property_placeholder_image` field. We
  // normalise to PNG *before* uploading so the asset stored in S3 (and inlined
  // by /property-qr-context on the next page load) is one @react-pdf can render.
  const handlePlaceholderUpload = async (file) => {
    if (!file) return;
    setPlaceholderError("");
    setPlaceholderUploading(true);
    try {
      const pngDataUrl = await fileToPngDataUrl(file);
      if (!pngDataUrl) {
        setPlaceholderError("Couldn't read that image. Try a PNG or JPG.");
        return;
      }
      const formData = new FormData();
      formData.append("file", dataUrlToBlob(pngDataUrl), "placeholder.png");
      formData.append("field", "property_placeholder_image");
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/update-agent-image`,
        formData
      );
      if (res.data && res.data.success && res.data.url) {
        setPlaceholderDataUrl(pngDataUrl);
      } else {
        setPlaceholderError(
          (res.data && res.data.message) || "Upload failed. Please try again."
        );
      }
    } catch {
      setPlaceholderError("Upload failed. Please try again.");
    } finally {
      setPlaceholderUploading(false);
      if (placeholderInputRef.current) placeholderInputRef.current.value = "";
    }
  };

  // Build a sample (agent branding + one with-photo + one no-photo property)
  // once properties land. The previews use this so picker tiles show a real
  // render rather than a static mockup.
  useEffect(() => {
    if (properties.length === 0 || sample) return undefined;
    const reqId = ++sampleReqRef.current;
    (async () => {
      try {
        const src = properties.find((p) => p.photo_url_full) || properties[0];
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/property-qr-context`,
          { external_properties: [src] }
        );
        if (reqId !== sampleReqRef.current) return;
        const ctx = res.data || {};
        const agentRaw = ctx.agent || {};
        const host = ctx.qr_host || "https://www.homeprice.site";
        const slug = agentRaw.landing_page_slug || "";
        if (agentRaw.agent_font_url) setAgentFontUrl(agentRaw.agent_font_url);
        // The backend tries to inline this to a data URL, but falls back to the
        // raw S3 URL when its own fetch fails — and @react-pdf can't render a
        // remote URL (blank in the PDF and the preview tiles). Run it through
        // urlToDataUrl so a raw URL becomes a renderable data URL client-side.
        // Keep any freshly uploaded value the agent set this session.
        if (agentRaw.property_placeholder_image) {
          const placeholderData = await urlToDataUrl(
            agentRaw.property_placeholder_image
          );
          if (reqId !== sampleReqRef.current) return;
          if (placeholderData) {
            setPlaceholderDataUrl((prev) => prev || placeholderData);
          }
        }
        const [logoDataUrl, profileDataUrl] = await Promise.all([
          urlToDataUrl(agentRaw.logo_url),
          urlToDataUrl(agentRaw.profile_image_url),
        ]);
        const agentBranded = {
          ...agentRaw,
          logo_url: logoDataUrl || "",
          profile_image_url: profileDataUrl || "",
        };
        const ctxP = (ctx.properties || [])[0];
        if (!ctxP) return;
        const profileSlug = (ctxP.profile_url || "")
          .split("/property-profile/")
          .pop()
          .replace(/\/$/, "");
        const slugPart = profileSlug ? `${profileSlug}-` : "";
        const url = `${host}/p/${slugPart}${ctxP.obfuscated_id}?agent=${encodeURIComponent(slug)}&campaign=preview`;
        const [qr_data_url, photoDataUrl] = await Promise.all([
          QRCode.toDataURL(url, { errorCorrectionLevel: "M", margin: 1, scale: 8 }),
          urlToDataUrl(ctxP.photo_url_full),
        ]);
        const withImage = { ...ctxP, qr_data_url, photo_url_full: photoDataUrl || "" };
        const withoutImage = {
          ...withImage,
          obfuscated_id: `${withImage.obfuscated_id}__noimg`,
          photo_url_full: "",
        };
        if (reqId !== sampleReqRef.current) return;
        setSample({ agent: agentBranded, properties: [withImage, withoutImage] });
      } catch {
        // Silent — design picker just falls back to name-only tiles.
      }
    })();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties]);

  // Render one 2-page PDF per design (with-image then without-image) whenever
  // the sample or no-image background toggle changes. Old blob URLs are
  // revoked when replaced.
  useEffect(() => {
    if (!sample) return undefined;
    const reqId = ++previewReqRef.current;
    const previewCopy = {
      ...DEFAULT_PROPERTY_QR_COPY,
      noImageMode: copy.noImageMode,
    };
    (async () => {
      const urls = {};
      // Render built-ins via PropertyQrDocument and custom designs via the
      // sandboxed JSX runtime. Either can throw — skip the offender and
      // continue so one bad custom design doesn't kill the whole picker.
      const colour = sample.agent.agent_colour || "#2563eb";
      const hexToRgb = (hex) => {
        const h = (hex || "").replace("#", "").padEnd(6, "0").slice(0, 6);
        return {
          r: parseInt(h.slice(0, 2), 16),
          g: parseInt(h.slice(2, 4), 16),
          b: parseInt(h.slice(4, 6), 16),
        };
      };
      const darken = (hex, factor = 0.7) => {
        const { r, g, b } = hexToRgb(hex);
        const f = (n) =>
          Math.max(0, Math.min(255, Math.round(n * factor)))
            .toString(16)
            .padStart(2, "0");
        return `#${f(r)}${f(g)}${f(b)}`;
      };
      const pickBannerText = (hex) => {
        const { r, g, b } = hexToRgb(hex);
        const lin = (c) => {
          const cc = c / 255;
          return cc <= 0.04045 ? cc / 12.92 : Math.pow((cc + 0.055) / 1.055, 2.4);
        };
        const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
        return lum > 0.4 ? "#1a1a1a" : "#ffffff";
      };
      const colourDark = darken(colour, 0.7);
      const bannerText = pickBannerText(colour);
      const firstName = (sample.agent.agent_name || "your agent").split(" ")[0];
      const inPersonCta = `Call ${firstName} on ${sample.agent.agent_phone || ""}`;

      const previewFont = useAgentFont && agentFontReady ? AGENT_FONT_FAMILY : undefined;
      // Feed the placeholder into the no-photo sample so the previews show the
      // unified hero layout the agent will actually get.
      const previewProps = placeholderDataUrl
        ? sample.properties.map((p) =>
            p.photo_url_full ? p : { ...p, photo_url_full: placeholderDataUrl }
          )
        : sample.properties;
      for (const d of PROPERTY_QR_DESIGNS) {
        try {
          const blob = await pdf(
            <PropertyQrDocument
              agent={sample.agent}
              properties={previewProps}
              copy={previewCopy}
              design={d.id}
              agentFontFamily={previewFont}
            />
          ).toBlob();
          if (reqId !== previewReqRef.current) return;
          urls[d.id] = URL.createObjectURL(blob);
        } catch {
          // skip this design
        }
      }
      for (const cd of customDesigns) {
        try {
          const doc = buildCustomDocument({
            code: cd.code,
            agent: { ...sample.agent, agent_font_family: previewFont },
            properties: previewProps,
            copy: previewCopy,
            colour,
            colourDark,
            bannerText,
            inPersonCta,
            agentFontFamily: previewFont,
          });
          const blob = await pdf(doc).toBlob();
          if (reqId !== previewReqRef.current) return;
          urls[cd.id] = URL.createObjectURL(blob);
        } catch {
          // skip this custom design — the picker will show a "broken" tile
        }
      }
      if (reqId !== previewReqRef.current) {
        Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
        return;
      }
      setPreviewUrls((prev) => {
        Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
        return urls;
      });
    })();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sample, copy.noImageMode, customDesigns, useAgentFont, agentFontReady, placeholderDataUrl]);

  const handleGeneratePdf = async () => {
    if (selectedIds.size === 0) return;
    setPdfLoading(true);
    setPdfBlob(null);
    setPdfUrl(null);
    try {
      const orderedProps = filteredProperties.filter((p) =>
        selectedIds.has(p.obfuscated_id)
      );

      // 1. Fetch agent branding context (external mode — properties already
      //    fetched via /street-listing). The PDF itself is rendered in the
      //    browser via @react-pdf/renderer.
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/property-qr-context`,
        { external_properties: orderedProps }
      );
      const ctx = res.data || {};
      const agentRaw = ctx.agent || {};
      const host = ctx.qr_host || "https://www.homeprice.site";
      const slug = agentRaw.landing_page_slug || "";
      if (agentRaw.agent_font_url) setAgentFontUrl(agentRaw.agent_font_url);

      // 2. Pre-inline agent branding images so a broken/CORS-blocked URL
      //    can't cause the whole PDF render to reject.
      const [logoDataUrl, profileDataUrl] = await Promise.all([
        urlToDataUrl(agentRaw.logo_url),
        urlToDataUrl(agentRaw.profile_image_url),
      ]);
      const agent = {
        ...agentRaw,
        logo_url: logoDataUrl || "",
        profile_image_url: profileDataUrl || "",
      };

      // 3. For each property, generate a QR data-URL and inline the hero
      //    photo. Drop the photo silently if it can't be fetched.
      const withQr = await Promise.all(
        (ctx.properties || []).map(async (p) => {
          // Extract the address slug from Domain's profile_url so the
          // homeprice property page can resolve the property via Domain's
          // GraphQL (it splits the URL segment as `${slug}-${id}`).
          const profileSlug = (p.profile_url || "")
            .split("/property-profile/")
            .pop()
            .replace(/\/$/, "");
          const slugPart = profileSlug ? `${profileSlug}-` : "";
          const url = `${host}/p/${slugPart}${p.obfuscated_id}?agent=${encodeURIComponent(slug)}&campaign=qr_code_property`;
          const [qr_data_url, photoDataUrl] = await Promise.all([
            QRCode.toDataURL(url, {
              errorCorrectionLevel: "M",
              margin: 1,
              scale: 8,
            }),
            urlToDataUrl(p.photo_url_full),
          ]);
          return {
            ...p,
            // Fall back to the agent's placeholder so no-photo cards render the
            // same hero layout as cards that have a real photo.
            photo_url_full: photoDataUrl || placeholderDataUrl || "",
            qr_data_url,
          };
        })
      );

      // 4. Render the PDF document client-side and produce a blob. Custom
      //    designs run through the JSX sandbox; built-ins use the static
      //    PropertyQrDocument renderer.
      const customSel = findCustomDesign(design);
      let blob;
      if (customSel) {
        const colour = agent.agent_colour || "#2563eb";
        const hexToRgb = (hex) => {
          const h = (hex || "").replace("#", "").padEnd(6, "0").slice(0, 6);
          return {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
          };
        };
        const darken = (hex, factor = 0.7) => {
          const { r, g, b } = hexToRgb(hex);
          const f = (n) =>
            Math.max(0, Math.min(255, Math.round(n * factor)))
              .toString(16)
              .padStart(2, "0");
          return `#${f(r)}${f(g)}${f(b)}`;
        };
        const pickBannerText = (hex) => {
          const { r, g, b } = hexToRgb(hex);
          const lin = (c) => {
            const cc = c / 255;
            return cc <= 0.04045 ? cc / 12.92 : Math.pow((cc + 0.055) / 1.055, 2.4);
          };
          const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
          return lum > 0.4 ? "#1a1a1a" : "#ffffff";
        };
        const colourDark = darken(colour, 0.7);
        const bannerText = pickBannerText(colour);
        const firstName = (agent.agent_name || "your agent").split(" ")[0];
        const inPersonCta = (copy.inPersonCta || "")
          .replace("{name}", firstName)
          .replace("{phone}", agent.agent_phone || "");
        const outFont = useAgentFont && agentFontReady ? AGENT_FONT_FAMILY : undefined;
        const doc = buildCustomDocument({
          code: customSel.code,
          agent: { ...agent, agent_font_family: outFont },
          properties: withQr,
          copy: { ...DEFAULT_PROPERTY_QR_COPY, ...copy },
          colour,
          colourDark,
          bannerText,
          inPersonCta,
          agentFontFamily: outFont,
        });
        blob = await pdf(doc).toBlob();
      } else {
        const outFont = useAgentFont && agentFontReady ? AGENT_FONT_FAMILY : undefined;
        blob = await pdf(
          <PropertyQrDocument
            agent={agent}
            properties={withQr}
            copy={copy}
            design={design}
            agentFontFamily={outFont}
          />
        ).toBlob();
      }

      setPdfBlob(blob);
      setPdfUrl(window.URL.createObjectURL(blob));
    } catch (err) {
      console.error("Property QR PDF error", err);
      const detail = err && (err.message || String(err));
      alert(
        detail
          ? `Failed to generate PDF: ${detail}`
          : "Failed to generate PDF. Please try again."
      );
    }
    setPdfLoading(false);
  };

  const downloadPdf = () => {
    if (!pdfBlob) return;
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "property_qr_cards.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const totalCount = properties.length;
  const filteredCount = filteredProperties.length;
  const selectedCount = useMemo(
    () => filteredProperties.filter((p) => selectedIds.has(p.obfuscated_id)).length,
    [filteredProperties, selectedIds]
  );

  const card = useMemo(
    () => ({
      background: "#fff",
      borderRadius: 12,
      padding: isMobile ? 16 : 24,
      boxShadow: "0 2px 8px #0001",
      marginBottom: 20,
    }),
    [isMobile]
  );

  return (
    <div>
      {/* ── Intro ────────────────────────────── */}
      <div style={card}>
        <h3 style={{ fontWeight: 600, fontSize: 20, marginBottom: 6 }}>
          Per-Property QR Cards
        </h3>
        <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 0, lineHeight: 1.55 }}>
          Generate a print-ready PDF — one A4 page per property — each with a
          QR code that links scanners directly to that property's profile.
          Choose a street, set the number range, review the matching
          properties, and download.
        </p>
      </div>

      {/* ── Step 1: street search ─────────────── */}
      <div style={card}>
        <label style={labelStyle}>1. Find a street</label>
        <div ref={dropdownRef} style={{ position: "relative" }}>
          <div ref={inputWrapRef} style={{ position: "relative" }}>
            <FontAwesomeIcon
              icon={faSearch}
              style={{
                position: "absolute",
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9ca3af",
              }}
            />
            <input
              value={streetQuery}
              onChange={(e) => {
                setStreetQuery(e.target.value);
                setSelectedStreet(null);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Start typing a street name, suburb, or postcode…"
              style={{ ...inputStyle, paddingLeft: 38, paddingRight: 38 }}
            />
            {streetQuery && (
              <button
                onClick={handleClearStreet}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "#9ca3af",
                  cursor: "pointer",
                  padding: 4,
                }}
                title="Clear"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>

          {showDropdown && streetQuery.length >= 2 && !selectedStreet && dropdownRect && ReactDOM.createPortal(
            <div
              data-street-dropdown
              style={{
                position: "fixed",
                top: dropdownRect.top,
                left: dropdownRect.left,
                width: dropdownRect.width,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                maxHeight: 320,
                overflowY: "auto",
                zIndex: 10000,
              }}
            >
              {streetLoading && (
                <div style={{ padding: 14, color: "#6b7280", fontSize: 13 }}>
                  <Spinner animation="border" size="sm" /> Searching…
                </div>
              )}
              {!streetLoading && streetResults.length === 0 && (
                <div style={{ padding: 14, color: "#6b7280", fontSize: 13 }}>
                  No matches yet.
                </div>
              )}
              {!streetLoading &&
                streetResults.map((r, i) => (
                  <button
                    key={`${r.urlSlug || r.address}-${i}`}
                    onClick={() => handleSelectStreet(r)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "10px 14px",
                      background: "transparent",
                      border: "none",
                      borderBottom: "1px solid #f3f4f6",
                      cursor: "pointer",
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f9fafb")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      style={{ color: "#2563eb", marginTop: 3 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: "#111827", fontSize: 14 }}>
                        {r.address}
                      </div>
                    </div>
                  </button>
                ))}
            </div>,
            document.body
          )}
        </div>
      </div>

      {/* ── Loading state (auto-fetched on street select) ── */}
      {selectedStreet && propertyLoading && (
        <div style={{ ...card, display: "flex", alignItems: "center", gap: 10, color: "#6b7280", fontSize: 14 }}>
          <Spinner animation="border" size="sm" /> Loading properties on {selectedStreet.address}…
        </div>
      )}

      {selectedStreet && !propertyLoading && propertyError && (
        <div style={{ ...card, color: "#dc2626", fontSize: 14 }}>{propertyError}</div>
      )}

      {/* ── Step 2: filter + select properties ── */}
      {properties.length > 0 && (
        <div style={card}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>
                {filteredCount} of {totalCount} properties
              </h4>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                {selectedCount} selected · refine with filters below. Up to 100 properties per PDF.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() =>
                  setSelectedIds(
                    new Set(filteredProperties.map((p) => p.obfuscated_id))
                  )
                }
                style={ghostBtn}
              >
                Select all
              </button>
              <button onClick={() => setSelectedIds(new Set())} style={ghostBtn}>
                Clear
              </button>
            </div>
          </div>

          {/* Filter controls */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
              gap: 10,
              marginBottom: 12,
              padding: 12,
              background: "#f9fafb",
              borderRadius: 8,
            }}
          >
            <div>
              <label style={{ ...labelStyle, fontSize: 11, fontWeight: 500 }}>From #</label>
              <input
                type="number"
                inputMode="numeric"
                value={startNum}
                onChange={(e) => setStartNum(e.target.value)}
                placeholder="any"
                style={{ ...inputStyle, padding: "8px 10px" }}
              />
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: 11, fontWeight: 500 }}>To #</label>
              <input
                type="number"
                inputMode="numeric"
                value={endNum}
                onChange={(e) => setEndNum(e.target.value)}
                placeholder="any"
                style={{ ...inputStyle, padding: "8px 10px" }}
              />
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: 11, fontWeight: 500 }}>Min beds</label>
              <input
                type="number"
                inputMode="numeric"
                value={minBeds}
                onChange={(e) => setMinBeds(e.target.value)}
                placeholder="any"
                style={{ ...inputStyle, padding: "8px 10px" }}
              />
            </div>
            <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <div>
                <label style={{ ...labelStyle, fontSize: 11, fontWeight: 500 }}>Show</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[
                    { id: "all", label: "All" },
                    { id: "rented", label: "Rented" },
                    { id: "owner", label: "Owner occupied" },
                  ].map((opt) => {
                    const active = occupancyMode === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setOccupancyMode(opt.id)}
                        style={{
                          padding: "4px 10px",
                          borderRadius: 999,
                          border: `1px solid ${active ? "#2563eb" : "#e5e7eb"}`,
                          background: active ? "#eff6ff" : "#fff",
                          color: active ? "#2563eb" : "#374151",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: 11, fontWeight: 500 }}>Street numbers</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[
                    { id: "all", label: "All" },
                    { id: "odd", label: "Odd" },
                    { id: "even", label: "Even" },
                  ].map((opt) => {
                    const active = parityMode === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setParityMode(opt.id)}
                        style={{
                          padding: "4px 10px",
                          borderRadius: 999,
                          border: `1px solid ${active ? "#2563eb" : "#e5e7eb"}`,
                          background: active ? "#eff6ff" : "#fff",
                          color: active ? "#2563eb" : "#374151",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {availableTypes.length > 0 && (
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ ...labelStyle, fontSize: 11, fontWeight: 500 }}>Property type</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {availableTypes.map((t) => {
                    const active = propertyTypes.has(t);
                    return (
                      <button
                        key={t}
                        onClick={() => togglePropertyType(t)}
                        style={{
                          padding: "4px 10px",
                          borderRadius: 999,
                          border: `1px solid ${active ? "#2563eb" : "#e5e7eb"}`,
                          background: active ? "#eff6ff" : "#fff",
                          color: active ? "#2563eb" : "#374151",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 12,
              maxHeight: 480,
              overflowY: "auto",
              paddingRight: 4,
            }}
          >
            {filteredProperties.map((p) => {
              const checked = selectedIds.has(p.obfuscated_id);
              const addr = [
                p.flat_number ? `${p.flat_number}/${p.street_number}` : p.street_number,
                p.street_name,
                p.street_type_long || p.street_type,
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <label
                  key={p.obfuscated_id}
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: 10,
                    border: `1px solid ${checked ? "#2563eb" : "#e5e7eb"}`,
                    borderRadius: 10,
                    background: checked ? "#eff6ff" : "#fff",
                    cursor: "pointer",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleId(p.obfuscated_id)}
                    style={{ marginTop: 2, accentColor: "#2563eb" }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 13,
                          color: "#111827",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {addr}
                      </div>
                      {p.is_rental && (
                        <span
                          title="Most recent listing was a rental — likely currently rented"
                          style={{
                            flexShrink: 0,
                            background: "#fef3c7",
                            color: "#92400e",
                            border: "1px solid #fde68a",
                            borderRadius: 999,
                            padding: "1px 8px",
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 0.3,
                          }}
                        >
                          Rented
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>
                      {p.suburb}, {p.state} {p.postcode}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        marginTop: 5,
                        fontSize: 11,
                        color: "#374151",
                      }}
                    >
                      <span><FontAwesomeIcon icon={faBed} /> {p.bedrooms || "—"}</span>
                      <span><FontAwesomeIcon icon={faBath} /> {p.bathrooms || "—"}</span>
                      <span><FontAwesomeIcon icon={faCar} /> {p.carspaces || "—"}</span>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Placeholder image for properties without a photo */}
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: "#f9fafb",
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 4 }}>
              Placeholder image
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10 }}>
              Used in place of a missing property photo, so flyers for homes
              without a photo share the same layout as the rest. Only you see
              this — it's saved to your account.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 96,
                  height: 64,
                  borderRadius: 6,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {placeholderDataUrl ? (
                  <img
                    src={placeholderDataUrl}
                    alt="Placeholder preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>No image</span>
                )}
              </div>
              <div>
                <input
                  ref={placeholderInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif"
                  style={{ display: "none" }}
                  onChange={(e) => handlePlaceholderUpload(e.target.files?.[0])}
                />
                <button
                  type="button"
                  disabled={placeholderUploading}
                  onClick={() => placeholderInputRef.current?.click()}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 12px",
                    borderRadius: 6,
                    border: "1px solid #2563eb",
                    background: placeholderUploading ? "#93c5fd" : "#2563eb",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: placeholderUploading ? "default" : "pointer",
                  }}
                >
                  {placeholderUploading ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      Uploading…
                    </>
                  ) : placeholderDataUrl ? (
                    "Replace image"
                  ) : (
                    "Upload image"
                  )}
                </button>
                <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 6 }}>
                  PNG, JPG or GIF.
                </div>
                {placeholderError ? (
                  <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>
                    {placeholderError}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Design picker */}
          <div
            style={{
              marginTop: 16,
              padding: 12,
              background: "#f9fafb",
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 4 }}>
              Choose a design
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10 }}>
              Tap a preview to enlarge it. All designs share the same outer
              dimensions, so cut lines align across the bundle.
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)",
                gap: 12,
              }}
            >
              {allDesigns.map((d) => {
                const active = design === d.id;
                const url = previewUrls[d.id];
                const isCustom = d.kind === "custom";
                return (
                  <div
                    key={d.id}
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      border: `2px solid ${active ? "#2563eb" : "#e5e7eb"}`,
                      background: active ? "#eff6ff" : "#fff",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      position: "relative",
                    }}
                  >
                    <div
                      onClick={() => url && setModalDesign(d.id)}
                      style={{
                        cursor: url ? "zoom-in" : "default",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                      title={url ? "Click to enlarge" : "Loading preview…"}
                    >
                      <PdfThumb url={url} width={isMobile ? 130 : 200} />
                    </div>
                    <button
                      onClick={() => {
                        setDesign(d.id);
                        setPdfBlob(null);
                        setPdfUrl(null);
                      }}
                      style={{
                        width: "100%",
                        padding: "6px 8px",
                        borderRadius: 6,
                        border: `1px solid ${active ? "#2563eb" : "#e5e7eb"}`,
                        background: active ? "#2563eb" : "#fff",
                        color: active ? "#fff" : "#111827",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      {active ? `✓ ${d.name}` : d.name}
                    </button>
                    {isCustom ? (
                      <div style={{ display: "flex", gap: 6, width: "100%" }}>
                        <button
                          onClick={() =>
                            setCustomizeFor({ mode: "edit", design: d.custom })
                          }
                          style={{
                            flex: 1,
                            padding: "5px 8px",
                            borderRadius: 6,
                            border: "1px solid #e5e7eb",
                            background: "#fff",
                            color: "#374151",
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 5,
                          }}
                          title="Edit with another AI prompt"
                        >
                          <FontAwesomeIcon icon={faPenToSquare} /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCustom(d.id)}
                          style={{
                            padding: "5px 8px",
                            borderRadius: 6,
                            border: "1px solid #fecaca",
                            background: "#fff",
                            color: "#b91c1c",
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                          title="Delete this custom design"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() =>
                          setCustomizeFor({ mode: "new", base: d })
                        }
                        style={{
                          width: "100%",
                          padding: "5px 8px",
                          borderRadius: 6,
                          border: "1px dashed #7c3aed",
                          background: "#faf5ff",
                          color: "#6d28d9",
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                        }}
                        title="Use this as a starting point and customise with AI"
                      >
                        <FontAwesomeIcon icon={faMagicWandSparkles} /> Customise with AI
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Brand typography toggle. Hidden unless the agent has uploaded
                a custom font in Settings (registered with @react-pdf so it
                makes it into the PDF, mirrored into the DOM so the swatch
                preview shows the real face). */}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
              <label style={{ ...labelStyle, fontSize: 11, fontWeight: 500 }}>
                Brand typography
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: `1px solid ${useAgentFont ? "#2563eb" : "#e5e7eb"}`,
                    background: useAgentFont ? "#eff6ff" : "#fff",
                    color: agentFontUrl ? (useAgentFont ? "#2563eb" : "#374151") : "#9ca3af",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: agentFontUrl ? "pointer" : "not-allowed",
                    userSelect: "none",
                    opacity: agentFontUrl ? 1 : 0.7,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={agentFontUrl ? useAgentFont : false}
                    disabled={!agentFontUrl}
                    onChange={(e) => setUseAgentFont(e.target.checked)}
                    style={{ accentColor: "#2563eb" }}
                  />
                  Use my brand font
                </label>
                {agentFontUrl && (
                  <div
                    style={{
                      padding: "6px 12px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      background: "#fff",
                      fontFamily: agentFontReady ? `"${AGENT_FONT_FAMILY}", system-ui, sans-serif` : "system-ui, sans-serif",
                      fontSize: 18,
                      color: "#111827",
                      lineHeight: 1.2,
                    }}
                    title="Preview of your uploaded font"
                  >
                    Aa Bb Cc 123 — {(sample?.agent?.agency_name || "Your Agency")}
                  </div>
                )}
              </div>
              {!agentFontUrl && (
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
                  Add a brand font in the Branding section of Settings to enable this.
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={handleGeneratePdf}
              disabled={pdfLoading || selectedCount === 0 || selectedCount > 100}
              style={{
                background: "#7c3aed",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "12px 20px",
                fontSize: 14,
                fontWeight: 600,
                cursor:
                  pdfLoading || selectedCount === 0 || selectedCount > 100
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  pdfLoading || selectedCount === 0 || selectedCount > 100 ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {pdfLoading ? (
                <>
                  <Spinner animation="border" size="sm" /> Generating PDF…
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faFilePdf} /> Generate PDF ({selectedCount})
                </>
              )}
            </button>
            {selectedCount > 100 && (
              <div style={{ color: "#b45309", fontSize: 12 }}>
                Max 100 properties per PDF. Deselect {selectedCount - 100} to continue.
              </div>
            )}
            {pdfBlob && (
              <button
                onClick={downloadPdf}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "12px 20px",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <FontAwesomeIcon icon={faDownload} /> Download
              </button>
            )}
          </div>

          {pdfUrl && (
            <iframe
              title="Property QR PDF preview"
              src={pdfUrl}
              style={{
                width: "100%",
                height: isMobile ? 500 : 720,
                marginTop: 16,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: "#f9fafb",
              }}
            />
          )}
        </div>
      )}

      {selectedStreet && !propertyLoading && properties.length === 0 && !propertyError && (
        <div style={{ ...card, color: "#6b7280", fontSize: 14 }}>
          No properties found on {selectedStreet.address}.
        </div>
      )}

      {properties.length > 0 && filteredCount === 0 && (
        <div style={{ ...card, color: "#6b7280", fontSize: 14 }}>
          No properties match the current filters. Adjust or clear them above.
        </div>
      )}

      {customizeFor && (
        <CustomizeDesignModal
          isMobile={isMobile}
          mode={customizeFor.mode}
          baseDesign={customizeFor.base}
          existingDesign={customizeFor.design}
          sample={sample}
          // Hand the modal the existing preview URL so it can show "what
          // you're starting from" before the agent's first generation lands.
          initialPreviewUrl={
            customizeFor.mode === "edit"
              ? previewUrls[customizeFor.design?.id]
              : previewUrls[customizeFor.base?.id]
          }
          agentFontFamily={useAgentFont && agentFontReady ? AGENT_FONT_FAMILY : undefined}
          onClose={() => setCustomizeFor(null)}
          onSaved={(saved) => {
            setCustomDesigns((prev) => {
              const without = prev.filter((d) => d.id !== saved.id);
              return [
                {
                  id: saved.id,
                  name: saved.name,
                  base_design_id: saved.base_design_id,
                  prompt: saved.prompt,
                  code: saved.code,
                },
                ...without,
              ];
            });
            setDesign(saved.id);
            setPdfBlob(null);
            setPdfUrl(null);
            setCustomizeFor(null);
          }}
        />
      )}

      {modalDesign && (
        <DesignPreviewModal
          design={allDesigns.find((d) => d.id === modalDesign)}
          url={previewUrls[modalDesign]}
          isMobile={isMobile}
          isActive={design === modalDesign}
          onClose={() => setModalDesign(null)}
          onSelect={() => {
            setDesign(modalDesign);
            setPdfBlob(null);
            setPdfUrl(null);
            setModalDesign(null);
          }}
        />
      )}
    </div>
  );
}

function PdfThumb({ url, width = 150 }) {
  const aspect = 297 / 210;
  const height = Math.round(width * aspect);
  const nativeWidth = 600;
  const scale = width / nativeWidth;
  return (
    <div
      style={{
        width,
        height,
        overflow: "hidden",
        position: "relative",
        background: "#f3f4f6",
        borderRadius: 4,
        border: "1px solid #e5e7eb",
      }}
    >
      {url ? (
        <iframe
          title="design preview"
          src={`${url}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
          style={{
            width: nativeWidth,
            height: nativeWidth * aspect,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            pointerEvents: "none",
            background: "#fff",
          }}
        />
      ) : (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9ca3af",
            fontSize: 11,
          }}
        >
          <Spinner animation="border" size="sm" />
        </div>
      )}
    </div>
  );
}

function DesignPreviewModal({ design, url, isMobile, isActive, onClose, onSelect }) {
  if (!design) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.72)",
        zIndex: 20000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? 12 : 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 12,
          width: "min(900px, 95vw)",
          height: "min(820px, 94vh)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: 14,
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
              {design.name}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              Page 1 shows a property with a photo, page 2 shows one without.
            </div>
          </div>
          <button onClick={onClose} style={ghostBtn}>
            Close
          </button>
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            background: "#f3f4f6",
          }}
        >
          {url ? (
            <iframe
              title={`${design.name} preview`}
              src={url}
              style={{
                width: "100%",
                height: "100%",
                border: 0,
                background: "#f3f4f6",
              }}
            />
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 420,
                color: "#6b7280",
              }}
            >
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </div>
        <div
          style={{
            boxSizing: "border-box",
            width: "100%",
            flexShrink: 0,
            padding: 12,
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            onClick={onSelect}
            disabled={isActive}
            style={{
              background: isActive ? "#9ca3af" : "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 16px",
              fontSize: 13,
              fontWeight: 700,
              maxWidth: "100%",
              cursor: isActive ? "default" : "pointer",
            }}
          >
            {isActive ? "Currently selected" : "Use this design"}
          </button>
        </div>
      </div>
    </div>
  );
}

const ghostBtn = {
  background: "transparent",
  color: "#2563eb",
  border: "1px solid #e5e7eb",
  borderRadius: 6,
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};
